import { useState, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';

export const useChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 'init',
      role: 'ai',
      content: 'Tell us what you are struggling to explain, or just paste your website URL. We will find the exact frameworks and strategies you need to fix it.'
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substring(2, 15));

  const [emailRequested, setEmailRequested] = useState(false);
  const [emailProvided, setEmailProvided] = useState(false);

  const messageCountRef = useRef(0);

  const submitEmail = useCallback(async (email) => {
    setEmailProvided(true);
    try {
      await supabase.functions.invoke('chat-proxy', {
        body: {
          action: 'event',
          payload: {
            sessionId,
            email: email,
            message: email,
            event: 'email_provided'
          }
        }
      });
    } catch (e) {
      console.error("Secondary webhook error:", e);
    }
  }, [sessionId]);

  const sendMessage = useCallback(async (content) => {
    if (!content.trim()) return;
    const userMsg = { id: Date.now().toString(), role: 'user', content };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    const currentCount = messageCountRef.current;
    messageCountRef.current += 1;

    try {
      // Proxying the chat through Supabase Edge Functions
      const { data: responseData, error } = await supabase.functions.invoke('chat-proxy', {
        body: {
          action: 'chat',
          payload: { sessionId, message: content }
        }
      });

      if (error) throw error;

      const n8nData = responseData?.data;
      let aiContent = '';
      
      if (typeof n8nData === 'string') aiContent = n8nData;
      else if (Array.isArray(n8nData) && n8nData.length > 0) aiContent = n8nData[0].output || n8nData[0].text || n8nData[0].message || JSON.stringify(n8nData[0]);
      else if (n8nData) aiContent = n8nData.output || n8nData.text || n8nData.message || n8nData.response || JSON.stringify(n8nData);
      else aiContent = "Received empty response from server.";

      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'ai', content: aiContent }]);

      // 1. After chatbot's first reply, trigger the email request state
      if (currentCount === 0) {
        setEmailRequested(true);
        
        // Notify secondary webhook via edge function proxy
        supabase.functions.invoke('chat-proxy', {
          body: {
            action: 'event',
            payload: {
              sessionId, 
              event: 'first_reply_sent',
              question: content,
              answer: aiContent,
              datetime: new Date().toISOString()
            }
          }
        }).catch(e => console.error("Secondary webhook proxy error:", e));
      }

    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'ai', content: 'I encountered an error connecting to the server. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  return { 
    messages, 
    isLoading, 
    sendMessage,
    emailRequested,
    emailProvided,
    submitEmail
  };
};